import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface Statistics {
  total_initiatives: number
  active_initiatives: number
  completed_ytd: number
  on_track_pct: number
  total_budget_allocated: number
  budget_utilized_pct: number
}

interface Initiative {
  id: string
  name: string
  owner: string
  progress: number
  budget: number
  status: string
}

interface Goal {
  goal: string
  target_date: string
  progress: number
  status: string
}

export default function StrategicPlanningExecPage() {
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [initiatives, setInitiatives] = useState<Initiative[]>([])
  const [goals, setGoals] = useState<Goal[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, initRes, goalsRes] = await Promise.all([
          fetch('/api/strategicplanningexec/statistics'),
          fetch('/api/strategicplanningexec/initiatives'),
          fetch('/api/strategicplanningexec/goals')
        ])
        setStatistics(await statsRes.json())
        setInitiatives(await initRes.json())
        setGoals(await goalsRes.json())
      } catch (error) {
        console.error('Error fetching data:', error)
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [])

  const getStatusBadge = (status: string) => {
    const colors: Record<string, string> = {
      'On Track': 'bg-green-100 text-green-800',
      'At Risk': 'bg-yellow-100 text-yellow-800',
      'Ahead': 'bg-blue-100 text-blue-800',
      'Behind': 'bg-red-100 text-red-800'
    }
    return colors[status] || 'bg-gray-100 text-gray-800'
  }

  if (loading) {
    return <div className="p-8"><Header /><div className="mt-8">Loading Strategic Planning...</div></div>
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="p-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">Strategic Planning</h1>

        {statistics && (
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-blue-600">{statistics.total_initiatives}</div>
              <div className="text-sm text-gray-500">Total Initiatives</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-green-600">{statistics.active_initiatives}</div>
              <div className="text-sm text-gray-500">Active</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-purple-600">{statistics.completed_ytd}</div>
              <div className="text-sm text-gray-500">Completed YTD</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-indigo-600">{statistics.on_track_pct}%</div>
              <div className="text-sm text-gray-500">On Track</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-orange-600">${(statistics.total_budget_allocated / 1000000).toFixed(0)}M</div>
              <div className="text-sm text-gray-500">Budget</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-teal-600">{statistics.budget_utilized_pct}%</div>
              <div className="text-sm text-gray-500">Utilized</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b"><h2 className="text-lg font-semibold">Strategic Initiatives</h2></div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Initiative</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Owner</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Progress</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {initiatives.map((i) => (
                    <tr key={i.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium">{i.name}</td>
                      <td className="px-4 py-3 text-gray-600">{i.owner}</td>
                      <td className="px-4 py-3">
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div className="bg-blue-600 h-2 rounded-full" style={{width: `${i.progress}%`}}></div>
                        </div>
                        <span className="text-xs text-gray-500">{i.progress}%</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs ${getStatusBadge(i.status)}`}>
                          {i.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b"><h2 className="text-lg font-semibold">Strategic Goals</h2></div>
            <div className="p-4 space-y-4">
              {goals.map((g, idx) => (
                <div key={idx} className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="font-medium">{g.goal}</span>
                    <span className={`px-2 py-1 rounded-full text-xs ${getStatusBadge(g.status)}`}>
                      {g.status}
                    </span>
                  </div>
                  <div className="text-sm text-gray-500 mt-1">Target: {g.target_date}</div>
                  <div className="mt-2">
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className="bg-green-600 h-2 rounded-full" style={{width: `${g.progress}%`}}></div>
                    </div>
                    <span className="text-xs text-gray-500">{g.progress}% complete</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
